/**
 * 「今日は聞くだけ」— 声を出さずに30秒で終わる入口。
 *
 * 狙いは2回目の起動。無料枠3回は「3回のうちに価値を分からせる」賭けなので、
 * 1回で消えるとその賭けが成立しない。声を出せる場所がないことが起動を止めている、
 * という仮説への対策であり、この入口の利用率がその答え合わせも兼ねる。
 *
 * 設計上の制約（変えるなら理由を確認すること）:
 *  - Gemini Live を使わない。台本も音声も固定。実行時のAI原価はゼロ
 *  - 無料枠を消費しない。原価ゼロだから成立する（乱用されても痛くない）
 *  - 2往復で必ず終わる。入口であって本編の代替にしない
 *  - 声を出す操作を置かない。マイクの許可も求めない
 *  - 渡すのは英語表現ではなく「型」。choice.pattern が本体で、en はその例
 */

export type ListenOnlyChoice = {
  /** 渡したい会話の型。英文よりこちらが本体。 */
  pattern: string;
  en: string;
  /** 音声ファイルの識別子（拡張子なし） */
  audioId: string;
};

export type ListenOnlyStep = {
  partner: { en: string; ja: string; audioId: string };
  choices: ListenOnlyChoice[];
};

export type ListenOnlyScript = {
  scenarioId: string;
  partnerName: string;
  /** 常に2つ。増やすと「入口」ではなくなる。 */
  steps: ListenOnlyStep[];
};

/**
 * まず1本だけ用意する。6本に広げるのは、この入口が効くと分かってから。
 * 台本と音声の制作量が6倍になるため、先に作らない。
 */
export const LISTEN_ONLY_SCRIPTS: ListenOnlyScript[] = [
  {
    scenarioId: "kitchen-monday",
    partnerName: "Mike",
    steps: [
      {
        partner: {
          en: "Morning! How was your weekend?",
          ja: "おはよう。週末はどうだった？",
          audioId: "s1-partner",
        },
        choices: [
          {
            pattern: "一言返して、同じ質問を返す",
            en: "It was good. How about you?",
            audioId: "s1-a",
          },
          {
            pattern: "したことを一つ足す",
            en: "I went to a cafe with my family.",
            audioId: "s1-b",
          },
          {
            pattern: "言葉に詰まったときの間の埋め方",
            en: "Hmm, let me think... it was quiet.",
            audioId: "s1-c",
          },
        ],
      },
      {
        partner: {
          en: "Mine was good too. I finally tried that new ramen place.",
          ja: "こっちも良かったよ。前から気になってたラーメン屋に、やっと行ったんだ。",
          audioId: "s2-partner",
        },
        choices: [
          {
            pattern: "「それいいね」と反応してから聞く",
            en: "Oh nice, how was it?",
            audioId: "s2-a",
          },
          {
            pattern: "自分も同じだと伝える",
            en: "I love ramen too.",
            audioId: "s2-b",
          },
          {
            pattern: "知らないと正直に言う",
            en: "I've never been there.",
            audioId: "s2-c",
          },
        ],
      },
    ],
  },
];

export function getListenOnlyScript(
  scenarioId: string
): ListenOnlyScript | undefined {
  return LISTEN_ONLY_SCRIPTS.find((s) => s.scenarioId === scenarioId);
}

/** 入口として最初に見せる台本。一覧のカードから開く。 */
export const LISTEN_ONLY_DEFAULT_SCENARIO_ID = "kitchen-monday";

/**
 * 音声ファイルの置き場所。Pages の静的配信に乗せる。
 * R2 は使わない（1シナリオ8ファイル・数百KB規模で、CDNに乗れば十分なため）。
 */
export function listenOnlyAudioSrc(
  scenarioId: string,
  audioId: string
): string {
  return `/audio/listen-only/${scenarioId}/${audioId}.wav`;
}
